import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import NiceModal from "@ebay/nice-modal-react";
import { useTranslate } from "hooks/useTranslate";
import type { ProductInput } from "stores/product";
import { NodeKinds } from "interfaces/enums";
import type { Node } from "interfaces/schemas";
import { useProjectActions } from "stores/project";
import {
  useProductOutput,
  useProductNodeId,
  useProductActions,
} from "stores/product";
import { toast } from "react-toastify";
import type { NodeApi } from "@hogartrabe/react-arborist-vrh";

type ConfiguratorPopUpContextValue = {
  setParams: React.Dispatch<React.SetStateAction<HandleUpdateParams | null>>;
  showConfiguratorPopup: (navFunc: () => void) => void;
  disableExit: boolean;
  setDisableExit: React.Dispatch<React.SetStateAction<boolean>>;
  navFuncState: (() => void) | null;
  handleUpdate: () => void;
  isUpdateButtonActive: boolean;
  setIsUpdateButtonActive: React.Dispatch<React.SetStateAction<boolean>>;
};

export const ConfiguratorPopUpContext =
  createContext<ConfiguratorPopUpContextValue | null>(null);

type ConfiguratorPopupProviderProps = {
  children: React.ReactNode;
};

type HandleUpdateParams = {
  id: string;
  kind: NodeKinds | string;
  productRequestData?: ProductInput;
} & Partial<Node>;

export const ConfiguratorPopupProvider = ({
  children,
}: ConfiguratorPopupProviderProps) => {
  const translate = useTranslate();
  const projectActions = useProjectActions();
  const productOutput = useProductOutput();
  const productNodeId = useProductNodeId();
  const productActions = useProductActions();
  const [params, setParams] = useState<HandleUpdateParams | null>(null);
  const [navFuncState, setNavFunc] = useState<(() => void) | null>(null);
  const [disableExit, setDisableExit] = useState<boolean>(false);
  const [isUpdateButtonActive, setIsUpdateButtonActive] =
    useState<boolean>(false);

  const handleUpdate = useCallback(() => {
    if (!productNodeId && !params?.id) {
      toast.error(translate("SAVE_PRODUCT_DATA_ERROR"));
      navFuncState?.();
      return;
    }
    if (params?.kind === NodeKinds.PRODUCT && !productOutput) {
      toast.error(translate("SAVE_PRODUCT_DATA_ERROR"));
      navFuncState?.();
      return;
    }
    if (params?.kind === NodeKinds.PRODUCT && productNodeId && productOutput) {
      projectActions.tree.updateNodeConfiguration({
        nodeId: productNodeId,
        productOutput,
        callback: () => navFuncState?.(),
      });
      productActions.syncAfterProductNodeUpdate();
      return;
    }
    if (params?.kind === NodeKinds.CUSTOM_PRODUCT) {
      const { productRequestData: _productRequestData, ...nodeArgs } = params;
      projectActions.tree.updateNode(
        {
          id: params.id,
          data: nodeArgs as Node,
        } as NodeApi<Node>,
        nodeArgs,
      );
      navFuncState?.();
      return;
    }
  }, [
    productNodeId,
    params,
    productOutput,
    productActions,
    navFuncState,
    translate,
    projectActions.tree,
  ]);

  const showConfiguratorPopup = useCallback(
    (navFunc: () => void) => {
      setNavFunc(() => navFunc); // save function argument reference
      NiceModal.show("confirmation", {
        iconName: "save-regular",
        title: translate("TEXT_SAVE_CHANGES"),
        alertText: translate("SAVE_PRODUCT_DATA_CONFIRMATION_ALERT"),
        submitBtnText: translate("SAVE"),
        onCancel: () => {
          setIsUpdateButtonActive(false);
          navFunc();
        },
      }).finally(() => {
        setParams(null);
        setNavFunc(null);
        setDisableExit(false);
        setIsUpdateButtonActive(false);
      });
    },
    [translate],
  );

  const context = useMemo(
    () => ({
      setParams,
      showConfiguratorPopup,
      disableExit,
      setDisableExit,
      navFuncState,
      handleUpdate,
      isUpdateButtonActive,
      setIsUpdateButtonActive,
    }),
    [
      disableExit,
      handleUpdate,
      navFuncState,
      showConfiguratorPopup,
      isUpdateButtonActive,
    ],
  );

  return (
    <ConfiguratorPopUpContext.Provider value={context}>
      {children}
    </ConfiguratorPopUpContext.Provider>
  );
};
export const useConfiguratorPopupContext = () => {
  const context = useContext(ConfiguratorPopUpContext);

  if (!context) {
    throw new Error(
      "useConfiguratorPopupContext must be used within ConfiguratorPopupProvider",
    );
  }

  return context;
};
